/**
 * A user's long-running bulk action, as projected by a BFF from a Workflow.
 *
 * Declared **structurally**, never imported from a generated schema: Janus and
 * Portus have separate OpenAPI schemas (`@punto/api-client/schema` vs
 * `schema-portus`), so shared code cannot reference either. Both Janus's
 * `BulkActionProgressDto` and Portus's `ReportBulkActionDto` satisfy this shape
 * without this package knowing anything about certificates or reports.
 *
 * `type` and `status` are widened to `string` on purpose — each app's generated
 * literal unions are assignable to them, and the panel only ever compares or
 * looks them up.
 */
export interface BulkActionProgressItem {
  referenceId: string;
  referenceLabel?: string;
  action: string;
  outcome: "warning" | "failed";
  error?: string;
}

export interface BulkActionProgressCounts {
  succeeded: number;
  skipped: number;
  warning: number;
  failed: number;
}

/** A message about the whole batch: a code the app translates, and the items it names. */
export interface BulkActionWarning {
  code: string;
  labels: string[];
}

export interface BulkActionProgress {
  total: number;
  processed: number;
  counts: BulkActionProgressCounts;
  incidents: BulkActionProgressItem[];
  warnings?: BulkActionWarning[];
}

export interface BulkAction {
  id: string;
  type: string;
  /** The entity the action is about (e.g. a folder id). Optional: not every BFF sends it. */
  referenceId?: string;
  status: string;
  progress?: BulkActionProgress;
  startedAt: string;
  completedAt?: string;
  errorMessage?: string;
  downloadUrl?: string;
}

/**
 * What a host does for one bulk-action type. Each type brings its own
 * behaviour, so the provider and the panel stay generic: they look the
 * handlers up by `action.type` and never branch on what the type is.
 */
export interface BulkActionTypeHandlers {
  /**
   * Called once when an action **this session triggered** reaches a terminal
   * status — never for an id rehydrated from `localStorage`. That session
   * scoping is what makes acting on a finish safe: a tray card is kept until it
   * is dismissed, so a handler that fired on merely *observing* a finished
   * action would re-run on every reload, and again on every keep-alive poll.
   */
  onSettled?: (action: BulkAction) => void;
  /** Called when the user follows the action's download link in the panel. */
  onDownload?: (action: BulkAction) => void;
}

/**
 * Handlers keyed by bulk-action type; unmapped types get none. Pass a stable
 * reference (module scope, or `useMemo`).
 */
export type BulkActionHandlersByType = Readonly<
  Record<string, BulkActionTypeHandlers>
>;

export const isBulkActionRunning = (action: BulkAction): boolean =>
  action.status === "in_progress";

export const isBulkActionFinished = (action: BulkAction): boolean =>
  action.status === "completed" ||
  action.status === "failed" ||
  action.status === "cancelled";
